import { selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailEditorPanel from 'features/EmailEditor/EmailEditorPanel'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { ReactComponent as CloseIcon } from './close.svg'
import ConfigurationStep from './ConfigurationStep'
import styles from './EditActionDialog.module.scss'

const steps = {
  configuration: 'configuration',
  textContent: 'textContent',
  email: 'email'
}

const stepNames = {
  configuration: 'Configuration',
  textContent: 'Text content',
  email: 'Email'
}

const requiredTagIds = ['__hotelName__', '__guestName__']

const mediaTag = mediaTags.campaign

function EditActionDialogInner({
  title,
  onClose,
  onSave,
  isSaving,
  initialData,
  showConfigurationStep = true
}) {
  const availableSteps = showConfigurationStep
    ? Object.keys(steps)
    : Object.keys(steps).filter(step => step !== 'configuration')

  const defaultStep = showConfigurationStep ? steps.configuration : steps.textContent
  const [activeStep, setActiveStep] = useState(defaultStep)

  // Get user data from Redux
  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)

  // Configuration state
  const [isEnabled, setIsEnabled] = useState(initialData?.isEnabled ?? true)
  const [sendAfterValue, setSendAfterValue] = useState(initialData?.sendAfterValue ?? 2)
  const [sendAfterUnit, setSendAfterUnit] = useState(initialData?.sendAfterUnit ?? 'days')
  const [medium, setMedium] = useState(initialData?.medium ?? 'EmailAndText')

  // Text content state
  const [messageTemplate, setMessageTemplate] = useState(
    initialData?.messageTemplate ??
      'Hi @[firstName](__guestName__), thank you for signing up at @[hotelName](__hotelName__)! We look forward to hosting you soon.'
  )
  const [messageMedia, setMessageMedia] = useState(initialData?.messageMedia ?? null)
  const [usedAllTags, setUsedAllTags] = useState(true)

  // Email editor context
  const { emailHtml, refreshEmailData, saveChanges, hasUnsavedChanges } = useEmailEditor()

  const handleTextChange = text => {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const handleSave = async () => {
    if (hasUnsavedChanges) {
      await saveChanges()
    }
    onSave?.({
      isEnabled,
      sendAfterValue,
      sendAfterUnit,
      medium,
      messageTemplate,
      messageMedia,
      emailHtml
    })
  }

  const handleDiscard = () => {
    onClose?.()
  }

  return (
    <Dialog>
      <div className={styles.resolutionWrapper}>
        <div className={styles.dialogWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>

          <div className={styles.tabs}>
            {availableSteps.map(step => (
              <button
                key={step}
                onClick={() => setActiveStep(step)}
                className={step === activeStep ? styles.activeTab : styles.tab}
              >
                {stepNames[step]}
              </button>
            ))}
          </div>

          <div className={styles.content}>
            {activeStep === steps.configuration && showConfigurationStep && (
              <ConfigurationStep
                isEnabled={isEnabled}
                setIsEnabled={setIsEnabled}
                sendAfterValue={sendAfterValue}
                setSendAfterValue={setSendAfterValue}
                sendAfterUnit={sendAfterUnit}
                setSendAfterUnit={setSendAfterUnit}
                medium={medium}
                setMedium={setMedium}
              />
            )}

            {activeStep === steps.textContent && (
              <div className={styles.textContentWrapper}>
                <MainTemplate
                  messageTemplate={messageTemplate}
                  onTextChange={handleTextChange}
                  usedAllTags={usedAllTags}
                  setUsedAllTags={setUsedAllTags}
                  setMessageMedia={setMessageMedia}
                  messageMedia={messageMedia}
                  isAllTagsRequired={true}
                  mediaTag={mediaTag}
                  requiredTagIds={requiredTagIds}
                  isMediaRequired={false}
                  customUrl={null}
                  setCustomUrl={() => {}}
                  isCustomUrlAllowed={false}
                />
              </div>
            )}

            {activeStep === steps.email && (
              <div className={styles.emailEditorWrapper}>
                <EmailEditorPanel
                  onSaveAndClose={handleSave}
                  onClose={refreshEmailData}
                />
              </div>
            )}
          </div>

          {activeStep !== steps.email && (
            <div className={styles.footer}>
              <SecondaryRegularButton onClick={handleDiscard} disabled={isSaving}>
                Discard
              </SecondaryRegularButton>
              <PrimaryRegularButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save changes'}
              </PrimaryRegularButton>
            </div>
          )}
        </div>

        <div className={styles.previewHolder}>
          {activeStep !== steps.email && (
            <TextPreview>
              <TextPreviewBubble
                defaultData={{ userName, hotelName }}
                hasLink
                text={messageTemplate}
              />
            </TextPreview>
          )}
          {activeStep === steps.email && (
            <EmailPreview>
              <TestEmail />
            </EmailPreview>
          )}
        </div>
      </div>
    </Dialog>
  )
}

function EditActionDialog({ contentId, contentCategory, ...props }) {
  return (
    <EmailEditorProvider contentId={contentId} contentCategory={contentCategory}>
      <EditActionDialogInner {...props} />
    </EmailEditorProvider>
  )
}

export default EditActionDialog
